// In-memory cache for slow-changing reference data (UOMs, tax codes, item groups,
// warehouses, sales reps, item/customer lists, …). These datasets are refetched by
// almost every form on mount; on tenants with thousands of rows that repeated
// network + parse cost is a big share of "opening a document feels slow".
//
// Design:
//   - cachedRequest(key, fetcher, ttlMs) returns a cached promise while fresh, and
//     also coalesces concurrent callers onto a single in-flight request.
//   - A rejected fetch is evicted so the next call retries (never caches an error).
//   - Mutations invalidate by key prefix (invalidateReference) so a create/update
//     is visible immediately instead of waiting out the TTL.
//   - clearReferenceCache() wipes everything — MUST be called on login/logout so a
//     different tenant never sees another tenant's cached reference data.
//
// Cached values are treated as read-only. Callers already consume responses
// immutably (setState + map/filter/find); do not mutate a cached response in place.

const store = new Map(); // key -> { at: epochMs, ttl: ms, promise }

const DEFAULT_TTL_MS = 5 * 60 * 1000; // 5 minutes

export function cachedRequest(key, fetcher, ttlMs = DEFAULT_TTL_MS) {
  const hit = store.get(key);
  if (hit && (Date.now() - hit.at) < hit.ttl) {
    return hit.promise;
  }

  const promise = Promise.resolve().then(fetcher);
  store.set(key, { at: Date.now(), ttl: ttlMs, promise });

  // Evict on failure so we retry next time instead of serving a cached rejection.
  promise.catch(() => {
    const current = store.get(key);
    if (current && current.promise === promise) store.delete(key);
  });

  return promise;
}

// Invalidate every key equal to `prefix` or beginning with `${prefix}:`.
// e.g. invalidateReference('inv:items') clears 'inv:items' and 'inv:items:{...}'.
export function invalidateReference(prefix) {
  if (!prefix) { store.clear(); return; }
  for (const key of [...store.keys()]) {
    if (key === prefix || key.startsWith(`${prefix}:`)) store.delete(key);
  }
}

export function clearReferenceCache() {
  store.clear();
}
